/**
 * FeedPage Component
 * Social activity feed with infinite scroll
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { Activity, RefreshCw } from 'lucide-react';
import { useFeed } from '../../shared/hooks/useFeed';
import { FeedItem } from './FeedItem';
import Button from '../UI/button';
import './FeedPage.css';

export const FeedPage: React.FC = () => {
  const { items, loading, error, hasMore, loadMore, refresh } = useFeed();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current && element) {
        observerRef.current.unobserve(element);
      }
    };
  }, [handleObserver]);

  const handleRefresh = async () => {
    await refresh();
  };

  if (loading && items.length === 0) {
    return (
      <div className='feed-page'>
        <Container>
          <div className='feed-loading'>
            <Spinner
              animation='border'
              variant='primary'
            />
            <p>Loading your feed...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className='feed-page'>
        <Container>
          <div className='feed-error'>
            <p>Failed to load feed</p>
            <Button
              onClick={handleRefresh}
              variant='primary'
            >
              Try Again
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className='feed-page'>
      <Container>
        <div className='feed-header'>
          <div className='feed-title-section'>
            <Activity size={32} />
            <h1 className='feed-title'>Social Feed</h1>
          </div>
          <Button
            onClick={handleRefresh}
            variant='outline-primary'
            disabled={loading}
            className='feed-refresh-btn'
          >
            <RefreshCw size={18} />
            Refresh
          </Button>
        </div>

        {items.length === 0 ? (
          <div className='feed-empty'>
            <Activity
              size={64}
              opacity={0.3}
            />
            <h3>No activity yet</h3>
            <p>
              Start following travelers and liking trips to see activity in your
              feed!
            </p>
          </div>
        ) : (
          <div className='feed-list'>
            {items.map((item) => (
              <FeedItem
                key={item._id}
                item={item}
              />
            ))}

            {/* Infinite scroll trigger */}
            <div
              ref={loadMoreRef}
              className='feed-load-more'
            >
              {loading && (
                <div className='feed-loading-more'>
                  <Spinner
                    animation='border'
                    size='sm'
                    variant='primary'
                  />
                  <span>Loading more...</span>
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div className='feed-end'>
                  <p>You're all caught up! 🎉</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};
